import { createContext, createRef, useContext, useMemo } from 'react';

import { Toast, ToastMessage } from 'primereact/toast';

import { ToastLife } from '@/components/hoc/toast-container/enums';

const context = createContext<React.RefObject<Toast> | null>(null);

// toast provider component without redux
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const ref = createRef<Toast>();
  return (
    <context.Provider value={ref}>
      <Toast ref={ref} />

      {children}
    </context.Provider>
  );
}

type Toaster = {
  /** show a toast notification */
  show(message: ToastMessage): ToastMessage;
  /**  update an existing toast notification */
  update(message: ToastMessage, update: ToastMessage): ToastMessage;
  /** remove a toast notification */
  remove(message: ToastMessage): void;
  /** show a toast notification for a promise */
  promise(
    promise: Promise<unknown>,
    messages: {
      loading: React.ReactNode;
      success: React.ReactNode;
      error: React.ReactNode | ((error: unknown) => React.ReactNode);
    },
  ): void;
};

function setDefaults(message: ToastMessage): ToastMessage {
  return Object.assign(message, {
    life: message.life ?? ToastLife.MEDIUM,
  });
}

export function useToast(): Toaster {
  const ref = useContext(context);
  return useMemo<Toaster>(
    () => ({
      show(message) {
        ref?.current?.show(setDefaults(message));
        return message;
      },
      update(message, update) {
        // update existing toast and trigger a refresh
        // TODO: move to a different toast library that supports updating existing toasts
        Object.assign(message, setDefaults(update));
        ref?.current?.show([]);
        return message;
      },
      remove(message) {
        ref?.current?.remove(message);
      },
      promise(promise, messages) {
        const msg = this.show({
          severity: 'info',
          summary: 'Loading...',
          detail: messages.loading,
          sticky: true,
        });

        promise
          .then(() => {
            this.update(msg, {
              severity: 'success',
              summary: '',
              detail: messages.success,
              sticky: false,
              life: ToastLife.MEDIUM,
            });
          })
          .catch((error: unknown) => {
            this.update(msg, {
              severity: 'error',
              summary: 'Error',
              detail:
                typeof messages.error === 'function'
                  ? messages.error(error)
                  : messages.error,
              sticky: false,
              life: ToastLife.MEDIUM,
            });
          });
      },
    }),
    [ref],
  );
}

export { ToastLife };
